import { siteInfo } from '@/mocks/siteInfo';

interface SchemaOrgProps {
  type: 'Organization' | 'WebSite' | 'WebPage';
  pageUrl?: string;
  pageTitle?: string;
  pageDescription?: string;
  datePublished?: string;
  dateModified?: string;
}

export function SchemaOrg({ type, pageUrl, pageTitle, pageDescription, datePublished, dateModified }: SchemaOrgProps) {
  let jsonLd: Record<string, unknown>;

  if (type === 'Organization') {
    jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: siteInfo.name,
      url: siteInfo.url,
      description: siteInfo.description,
      email: siteInfo.email,
      telephone: siteInfo.phone,
      address: {
        '@type': 'PostalAddress',
        streetAddress: '建国路88号八戒大厦18层',
        addressLocality: '北京',
        addressRegion: '朝阳区',
        addressCountry: 'CN',
      },
    };
  } else if (type === 'WebSite') {
    jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: siteInfo.name,
      url: siteInfo.url,
      description: siteInfo.description,
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${siteInfo.url}/search?q={search_term_string}`,
        },
        'query-input': 'required name=search_term_string',
      },
    };
  } else {
    jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: pageTitle,
      description: pageDescription,
      url: pageUrl ? `${siteInfo.url}${pageUrl}` : siteInfo.url,
      datePublished,
      dateModified: dateModified || new Date().toISOString().split('T')[0],
      isPartOf: {
        '@type': 'WebSite',
        name: siteInfo.name,
        url: siteInfo.url,
      },
    };
  }

  return <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>;
}

interface ArticleSchemaProps {
  title: string;
  description: string;
  url: string;
  imageUrl: string;
  datePublished: string;
  authorName?: string;
}

export function ArticleSchema({ title, description, url, imageUrl, datePublished, authorName }: ArticleSchemaProps) {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description,
    url: `${siteInfo.url}${url}`,
    image: imageUrl,
    datePublished,
    dateModified: new Date().toISOString().split('T')[0],
    author: {
      '@type': 'Organization',
      name: authorName || siteInfo.name,
    },
    publisher: {
      '@type': 'Organization',
      name: siteInfo.name,
      url: siteInfo.url,
    },
  };

  return <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>;
}

interface FAQSchemaProps {
  questions: { question: string; answer: string }[];
}

export function FAQSchema({ questions }: FAQSchemaProps) {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: questions.map((q) => ({
      '@type': 'Question',
      name: q.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: q.answer,
      },
    })),
  };

  return <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>;
}